import { useState, useEffect } from 'react';
import { useLocation, Redirect } from 'wouter';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import StripeCheckout from '@/components/subscription/stripe-checkout';

// Subscription plans - matching actual Stripe configuration
const plans = [
  {
    id: 2,
    name: 'Single',
    price: 29,
  },
  {
    id: 3,
    name: 'Team',
    price: 49,
  },
  {
    id: 4,
    name: 'Enterprise',
    price: 199,
  }
];

// Map numeric plan IDs to string IDs for the API
const planIdMapping = {
  2: 'Single',
  3: 'Team',
  4: 'Enterprise'
};

export default function CheckoutPage() {
  const [location, navigate] = useLocation();
  const { user, isAuthenticated, isLoading } = useAuth();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Get the plan ID, name, and priceId from URL
  const urlParams = new URLSearchParams(location.split('?')[1] || '');
  const planIdParam = urlParams.get('plan');
  const planNameParam = urlParams.get('planName');
  const priceId = urlParams.get('priceId');
  const planId = planIdParam ? parseInt(planIdParam) : null;
  
  // Find the plan details, or create a custom plan object if not found
  let selectedPlan = plans.find(p => p.id === planId);
  
  // If we have a plan name in the URL but no matching plan in our list, create a custom plan
  if (!selectedPlan && planNameParam && planIdParam) {
    console.log(`Creating custom plan with ID ${planIdParam} and name ${planNameParam}`);
    selectedPlan = {
      id: planId || parseInt(planIdParam),
      name: planNameParam,
      price: planId === 2 ? 29 : planId === 3 ? 99 : planId === 4 ? 299 : 0
    };
  }
  
  // If not logged in, redirect to auth
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isLoading, isAuthenticated, navigate]);
  
  // Handle subscription success
  const handleSuccess = () => {
    toast({
      title: 'Subscription activated',
      description: `Your ${selectedPlan?.name} subscription is now active!`,
    });
    
    // Redirect to payment success page with plan details
    setTimeout(() => {
      navigate(`/payment-success?planName=${selectedPlan?.name}&planId=${planId}`);
    }, 1000);
  };
  
  // Handle subscription cancellation
  const handleCancel = () => {
    toast({
      title: 'Purchase cancelled',
      description: 'Your subscription purchase was cancelled.',
      variant: 'destructive',
    });
    
    setIsProcessing(false);
  };
  
  // If still loading auth status, show loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // If no plan selected or invalid plan, redirect to pricing
  if (!planId || !selectedPlan) {
    return <Redirect to="/#pricing" />;
  }
  
  // For free plan, activate it with the API and redirect
  useEffect(() => {
    if (planId === 1 && isAuthenticated && !isLoading) {
      const activateFreePlan = async () => {
        try {
          const response = await fetch('/api/subscriptions/create', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              planId: 'free' 
            }),
          });
          
          if (response.ok) {
            toast({
              title: 'Free Plan Activated',
              description: 'Your account is ready to use!',
            });
            navigate('/dashboard');
          } else {
            const errorData = await response.json();
            toast({
              title: 'Error',
              description: errorData.error || 'Failed to activate free plan',
              variant: 'destructive',
            });
          }
        } catch (error) {
          console.error('Error activating free plan:', error);
          toast({
            title: 'Error',
            description: 'Failed to activate free plan. Please try again.',
            variant: 'destructive',
          });
        }
      };
      
      activateFreePlan();
    }
  }, [planId, isAuthenticated, isLoading, navigate, toast]);
  
  return (
    <div className="container max-w-md mx-auto py-12">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Complete Your Purchase</CardTitle>
          <CardDescription>
            Subscribe to the {selectedPlan.name} Plan
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span>Plan:</span>
              <span className="font-medium">{selectedPlan.name}</span>
            </div>
            
            <div className="flex justify-between">
              <span>Price:</span>
              <span className="font-medium">${selectedPlan.price}/month</span>
            </div>
            
            <Separator />
            
            <div className="flex justify-between font-bold">
              <span>Total:</span>
              <span>${selectedPlan.price}/month</span>
            </div>
          </div>
          
          <div className="mt-6">
            <StripeCheckout
              planId={planIdMapping[selectedPlan.id as keyof typeof planIdMapping] || selectedPlan.id}
              planName={selectedPlan.name}
              priceId={priceId || undefined}
              onSuccess={handleSuccess}
              onCancel={handleCancel}
            />
          </div>
        </CardContent>
        
        <CardFooter className="flex justify-center">
          <Button variant="link" onClick={() => navigate('/#pricing')}>
            Change plan
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}